
import React, { useState, useEffect } from 'react';
import { PlaylistCard } from '@/components/collections/PlaylistCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FolderHeart, Star } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

interface MyCollectionsTabProps {
  userPlaylists: any[] | null;
  followedPlaylists: any[] | null;
  isLoadingUserPlaylists: boolean;
  isLoadingFollowedPlaylists: boolean;
}

export const MyCollectionsTab = ({ 
  userPlaylists, 
  followedPlaylists, 
  isLoadingUserPlaylists, 
  isLoadingFollowedPlaylists 
}: MyCollectionsTabProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const subtabParam = searchParams.get('subtab');
  const [activeSubTab, setActiveSubTab] = useState(subtabParam === 'followed' ? 'followed' : 'created');
  
  useEffect(() => {
    // Update active subtab when URL parameters change
    if (subtabParam === 'followed') {
      setActiveSubTab('followed');
    } else if (subtabParam === 'created') {
      setActiveSubTab('created');
    }
  }, [subtabParam]);
  
  const handleTabChange = (value: string) => {
    setActiveSubTab(value);
    // Update URL when tab changes
    searchParams.set('subtab', value);
    setSearchParams(searchParams);
  };
  
  const NoUserPlaylists = () => (
    <div className="text-center py-12">
      <h3 className="text-lg font-semibold mb-2">You haven't created any collections yet</h3>
      <p className="text-gray-600">
        Create your first collection by clicking the "Create Collection" button
      </p>
    </div>
  );
  
  const NoFollowedPlaylists = () => (
    <div className="text-center py-12">
      <h3 className="text-lg font-semibold mb-2">You haven't followed any collections yet</h3>
      <p className="text-gray-600">
        Explore collections and click the follow button to add them here
      </p>
    </div>
  );

  if (isLoadingUserPlaylists || isLoadingFollowedPlaylists) return null;
  
  return (
    <div className="space-y-6">
      <Tabs value={activeSubTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4">
          <TabsTrigger value="created" className="flex items-center gap-2">
            <FolderHeart className="w-4 h-4" />
            Created by you
          </TabsTrigger>
          <TabsTrigger value="followed" className="flex items-center gap-2">
            <Star className="w-4 h-4" />
            Followed
          </TabsTrigger>
        </TabsList>

        <TabsContent value="created">
          {!userPlaylists?.length ? (
            <NoUserPlaylists />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {userPlaylists.map((playlist) => {
                return (
                  <PlaylistCard 
                    key={playlist.id}
                    id={playlist.id}
                    name={playlist.name}
                    description={playlist.description}
                    placesCount={playlist.places_count}
                    creatorName={playlist.created_by_user?.username}
                    creatorAvatar={playlist.created_by_user?.avatar_url}
                    image={playlist.image}
                    city={playlist.city}
                    country={playlist.country}
                    placeItems={playlist.place_items || []}
                  />
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="followed">
          {!followedPlaylists?.length ? (
            <NoFollowedPlaylists />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {followedPlaylists.map((playlist) => {
                return (
                  <PlaylistCard 
                    key={playlist.id}
                    id={playlist.id}
                    name={playlist.name}
                    description={playlist.description}
                    placesCount={playlist.places_count}
                    creatorName={playlist.created_by_user?.username}
                    creatorAvatar={playlist.created_by_user?.avatar_url}
                    image={playlist.image}
                    city={playlist.city}
                    country={playlist.country}
                    placeItems={playlist.place_items || []}
                  />
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
